import { QueryCache, QueryClient } from '@tanstack/react-query'
import { extractApiStatus, extractRetryAfterMs, isDpeViewUnavailable } from './apiError'

/** `Retry-After` absent ou illisible sur un 503 DPE_VIEW_UNAVAILABLE : relance dans 1 min. */
const DPE_VIEW_RETRY_FALLBACK_MS = 60_000
/** Au-delà, un 429 n'est pas réessayé en silence : l'écran d'erreur s'affiche. */
const RATE_LIMIT_MAX_WAIT_MS = 30_000

/** Délai avant de relancer un appel tombé sur la reconstruction de la vue DPE. */
export function dpeViewRetryDelayMs(error: unknown): number {
  return extractRetryAfterMs(error) ?? DPE_VIEW_RETRY_FALLBACK_MS
}

/**
 * 503 DPE_VIEW_UNAVAILABLE (reconstruction hebdo de la vue DPE) : aucune relance
 * immédiate — l'écran affiche tout de suite « Données en cours de mise à jour » —
 * mais la requête est relancée une fois le `Retry-After` écoulé, si un écran
 * l'observe encore et que rien ne l'a relancée entre-temps (sinon ce nouvel
 * échec a programmé sa propre relance). Partagé avec le client invité.
 */
export function createQueryCache(): QueryCache {
  return new QueryCache({
    onError: (error, query) => {
      if (!isDpeViewUnavailable(error)) return
      const failedAt = query.state.errorUpdatedAt
      setTimeout(() => {
        const { errorUpdatedAt, fetchStatus } = query.state
        if (errorUpdatedAt !== failedAt || fetchStatus !== 'idle' || !query.isActive()) return
        query.fetch().catch(() => {})
      }, dpeViewRetryDelayMs(error))
    },
  })
}

export const queryClient = new QueryClient({
  queryCache: createQueryCache(),
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        if (isDpeViewUnavailable(error)) return false
        const status = extractApiStatus(error)
        // 429 RATE_LIMITED (300 requêtes / 5 min par IP, toute l'API) : quelques
        // essais espacés, sauf si le back annonce une attente plus longue.
        if (status === 429) return failureCount < 3 && (extractRetryAfterMs(error) ?? 0) <= RATE_LIMIT_MAX_WAIT_MS
        if (status && status >= 400 && status < 500 && status !== 408) return false
        return failureCount < 2
      },
      // `Retry-After` quand le back le donne (endpoints publics), sinon backoff
      // exponentiel (1 s, 2 s, 4 s…) : le 429 global n'en envoie pas. Plafonné à
      // 30 s : une attente annoncée plus longue ne laisse pas l'écran en chargement.
      retryDelay: (failureCount, error) =>
        Math.min(extractRetryAfterMs(error) ?? 1000 * 2 ** failureCount, RATE_LIMIT_MAX_WAIT_MS),
      staleTime: 30_000,
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: false,
    },
  },
})
